import { createFileRoute } from "@tanstack/react-router";

import { GuidePage, type GuideFaq, type GuideSection } from "@/components/seo/GuidePage";
import { guidePageHead } from "@/lib/guide-page-seo";

const PATH = "/guides/meta-housing-rules-dubai-property";
const HEADLINE = "Meta's housing rules, when Dubai property is advertised to buyers abroad";
const TITLE = "Meta's Housing Ad Rules for Dubai Property Advertised Abroad | Adkiro";
const DESCRIPTION =
  "Property ads shown to audiences in the US, Canada or Europe must be declared as housing ads on Meta, which removes age, gender and postcode targeting. What that changes for a Dubai campaign aimed at buyers abroad.";

const KEY_POINTS = [
  "Meta requires campaigns that offer housing to be declared under its Special Ad Category when they reach people in the United States (since 4 December 2019), Canada (since 3 December 2020) or Europe (since 7 December 2021).",
  "The rule follows the audience, not the advertiser. A Dubai developer advertising apartments to people in London or Frankfurt is inside it; the same campaign shown only in the UAE is not.",
  "A declared campaign cannot choose age or gender, cannot target by postcode and cannot use lookalike audiences. Location targeting has a minimum radius: 15 miles in the US and Canada, and 15 kilometres in Europe by Meta's developer documentation.",
  "Meta may reject an ad that should have been declared and was not, and declaring late means changing a campaign that is already running.",
  "With those levers gone, the ad itself does the selecting: the price, the place and the payment plan bring the right buyer and send the wrong one past.",
];

const SECTIONS: GuideSection[] = [
  {
    heading: "Which campaigns the rule covers",
    body: [
      "Meta calls it the Special Ad Category. It applies to advertising for housing, employment and financial products, and for housing it covers ads that offer a home for sale or rent.",
      "Meta's developer documentation gives the dates. Businesses in the United States, or reaching people there, have had to declare housing campaigns since 4 December 2019. Advertisers reaching Canada have had to since 3 December 2020, and advertisers reaching Europe since 7 December 2021. Meta's list for Europe includes the United Kingdom and every member state of the European Union.",
      "The test is where the audience is. A developer in Dubai, a broker in Abu Dhabi and an agency in Sofia are all inside the rule the moment their property ad is set to reach someone in London, Lisbon, Toronto or Miami.",
    ],
  },
  {
    heading: "What a declared campaign cannot do",
    body: [
      "Declaring the category removes the targeting options most property marketers reach for first. The age range is fixed at 18 to 65 and over. Gender cannot be chosen. Postcodes are not available, and a location has to be drawn with a minimum radius, so a campaign cannot be pinned to one expensive street.",
      "Lookalike audiences are not available either, and Meta's help pages say that exclusions and some detailed interest options are limited. The system is built to stop a housing ad from being shown only to a narrow kind of person.",
    ],
    points: [
      "Age: fixed at 18 to 65+.",
      "Gender: all, with no selection.",
      "Location: no postcodes, and a minimum radius of 15 miles (25 km) in the US and Canada and 15 km in Europe by the developer documentation. Meta's help centre gives a slightly larger figure for Europe, so plan for the larger.",
      "Lookalike audiences: unavailable.",
    ],
  },
  {
    heading: "What still works",
    body: [
      "Location still works at the scale of a city and its surroundings, and so do an advertiser's own audiences: people who have enquired before, or who visited the project's page, can be reached again.",
      "What changes most is the job of the creative. When the audience cannot be narrowed by age, the ad has to do the narrowing. A price, a payment plan, a handover date and an honest picture of the property select the buyer who can afford it and let everyone else scroll on. A vague luxury ad with no price reaches the same broad audience and invites all of it to enquire.",
      "Measurement matters more for the same reason. If the campaign learns from every form submitted, a broad audience fills it with weak enquiries. Telling Meta which enquiries became viewings gives the system a better target than the form.",
    ],
  },
  {
    heading: "The same project, advertised in the UAE",
    body: [
      "The restriction does not apply everywhere. Meta's documentation says that a business outside the United States, reaching audiences outside the United States, Canada and Europe, may opt out of the category. A Dubai developer advertising to people in the UAE or the wider Gulf keeps the full set of targeting options.",
      "That argues for separate campaigns. One runs in the Gulf with the targeting it is allowed. Another is declared as housing and reaches buyers in Europe or North America within the rules. The category is set for a whole campaign, so mixing the two applies the restrictions to all of it.",
      "Dubai's own rules are separate from Meta's and apply in both cases: a property advertisement there needs a Land Department permit and its QR code.",
    ],
  },
  {
    heading: "What this guide is, and is not",
    body: [
      "This is a working guide for marketing teams, written from Meta's published documentation, which is listed below. Meta changes these rules without notice and its own pages do not always agree with each other. Read the current version before a campaign is built, and treat this page as a map rather than the territory.",
    ],
  },
];

const FAQS: GuideFaq[] = [
  {
    q: "Does a Dubai developer need Meta's housing category when advertising to buyers in the UK or Europe?",
    a: "Yes. Meta requires ads that offer housing to be declared under its Special Ad Category when they reach audiences in Europe, and its developer documentation dates that requirement to 7 December 2021. The rule follows where the audience is, not where the advertiser is based, so a Dubai developer showing property ads to people in London or Frankfurt is inside it.",
  },
  {
    q: "What targeting is lost under Meta's housing category?",
    a: "The age range is fixed at 18 to 65 and over, gender cannot be chosen, postcode targeting is not available and lookalike audiences cannot be used. Location targeting needs a minimum radius: 15 miles (25 km) in the United States and Canada and 15 km in Europe, according to Meta's developer documentation.",
  },
  {
    q: "Do property ads shown only in the UAE need the housing category?",
    a: "Meta's developer documentation says a business outside the United States that reaches audiences outside the United States, Canada and Europe may opt out of the Special Ad Category. A Dubai developer advertising only to people in the UAE or the wider Gulf can therefore keep full targeting. Dubai's own rules still apply: a property advertisement there needs a Land Department permit and its QR code.",
  },
  {
    q: "What happens if a housing ad is not declared?",
    a: "Meta's documentation says ads may be rejected if the advertiser does not choose the appropriate Special Ad Category. Declaring the category afterwards means changing a campaign that is already running, which costs the time and budget it had spent learning.",
  },
  {
    q: "How do you reach serious buyers when age and postcode targeting are gone?",
    a: "Through the ad and the measurement. An ad that states the price, the payment plan and the handover date selects buyers who can afford the property, where a vague luxury ad invites everyone. Reporting back to Meta which enquiries became viewings then teaches the campaign to look for more people like those buyers, instead of more people who fill in forms.",
  },
];

export const Route = createFileRoute("/guides/meta-housing-rules-dubai-property")({
  head: () =>
    guidePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      headline: HEADLINE,
      published: "2026-10-03",
      modified: "2026-10-03",
      faqs: FAQS,
    }),
  component: Page,
});

function Page() {
  return (
    <GuidePage
      eyebrow="Dubai property"
      headline={HEADLINE}
      standfirst="A property ad shown to people in the United States, Canada or Europe has to be declared as a housing ad on Meta, wherever the advertiser is based. For a Dubai project aimed at buyers in London or New York, that removes most of the targeting a marketer expects to use."
      keyPoints={KEY_POINTS}
      sections={SECTIONS}
      faqs={FAQS}
      updated="3 October 2026"
      readingMinutes={6}
      sources={[
        {
          href: "https://developers.facebook.com/documentation/ads-commerce/marketing-api/audiences/special-ad-category",
          label: "Meta developer documentation: Special Ad Category",
        },
        {
          href: "https://www.facebook.com/business/help/2220749868045706",
          label: "Meta Business Help Centre: audience restrictions for special ad categories",
        },
      ]}
      related={[
        {
          href: "/guides/dubai-property-ad-permit",
          label: "The Dubai property ad permit and its QR code, on Meta",
        },
        { href: "/meta-pixel-conversions-api", label: "Meta Pixel and Conversions API setup" },
        {
          href: "/real-estate-lead-generation-meta-ads",
          label: "Meta ads for property agencies and developers",
        },
      ]}
    />
  );
}
